import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { useVideoRingNotificationsContext } from '@/contexts/VideoRingNotificationsContext';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

export function IncomingVideoRingModal() {
  const { t } = useTranslation();
  const { incomingRing, acceptRing, rejectRing, isProcessing } = useVideoRingNotificationsContext();
  const [countdown, setCountdown] = useState(30);
  const rejectRingRef = useRef(rejectRing);

  // Keep ref in sync
  useEffect(() => {
    rejectRingRef.current = rejectRing;
  }, [rejectRing]);

  // Subscribe to ring request status changes
  useEffect(() => {
    if (!incomingRing) return;

    const channel = supabase
      .channel(`ring-request-${incomingRing.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'video_call_ring_requests',
          filter: `id=eq.${incomingRing.id}`,
        },
        (payload: any) => {
          const ringRequest = payload.new;
          
          // Close modal if status changed to timeout, accepted, or cancelled
          if (['timeout', 'accepted', 'cancelled'].includes(ringRequest.status)) {
            console.log('Ring request status changed to:', ringRequest.status);
            rejectRingRef.current();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [incomingRing?.id]); // Only depend on the ID

  useEffect(() => {
    if (!incomingRing) {
      setCountdown(30);
      return;
    }

    setCountdown(incomingRing.timeout_seconds);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          // A3) When countdown reaches 0, close the modal (cleanup without action)
          console.log('[IncomingVideoRingModal] ⏰ Countdown reached 0, closing modal');
          rejectRingRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [incomingRing?.id, incomingRing?.timeout_seconds]);

  if (!incomingRing) return null;

  const displayName = incomingRing.contact_name || incomingRing.lead_name || 'Unknown Contact';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Dialog open={true}>
      <DialogContent className="border-4 border-primary animate-pulse-slow max-w-md">
        <DialogTitle className="text-center text-2xl font-bold flex items-center justify-center gap-2">
          <Video className="h-6 w-6 text-primary" />
          {t('videoRing.incomingVideoCall')}
        </DialogTitle>

        <div className="space-y-6 py-4">
          {/* Contact Avatar */}
          <div className="flex justify-center">
            <Avatar className="h-24 w-24 ring-4 ring-primary/30 ring-offset-2 ring-offset-background">
              <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Contact Info */}
          <div className="text-center space-y-2">
            <h3 className="text-xl font-bold">{displayName}</h3>
            {incomingRing.sdr_name && (
              <p className="text-sm text-muted-foreground">
                {t('videoRing.requestedBy')} <span className="font-medium">{incomingRing.sdr_name}</span>
              </p>
            )}
            {incomingRing.title && (
              <p className="text-sm text-muted-foreground">{incomingRing.title}</p>
            )}
          </div>

          {/* Countdown */}
          <div className="text-center">
            <div className="text-5xl font-bold text-primary tabular-nums">{countdown}s</div>
            <p className="text-sm text-muted-foreground mt-1">{t('videoRing.toAnswer')}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={rejectRing}
              variant="outline"
              size="lg"
              disabled={isProcessing}
              className="flex-1 border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <PhoneOff className="mr-2 h-5 w-5" />
              {t('videoRing.decline')}
            </Button>
            <Button
              onClick={acceptRing}
              size="lg"
              disabled={isProcessing}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/30 hover:shadow-green-600/40 transition-all duration-200 hover:scale-[1.02]"
            >
              <Phone className="mr-2 h-5 w-5" />
              {t('videoRing.accept')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
